import moment from 'moment';
import pluralize from 'pluralize';
import lodashCompact from 'lodash/fp/compact';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import typeCheck from 'common/util/typeCheck';
import isBoolean from 'common/util/isBoolean';

import {
  getListingAvailableDateMoment,
  getListingAvailableDateRange,
  isListingTenantScreening,
} from 'common/listingHelpers';

import { numberWithCommas } from 'common/formatters';

import {
  getInitialAssemblyLineVOIEFormState,
  getAssemblyLineVoieStatuses,
} from 'common/householdApplication/incomeHelpers';

import {
  isApplicationPastStatus,
  isVerificationOfEmploymentCompleted,
  isVerificationOfRentalCompleted,
  getApplicationMonthlyIncome,
  isVerificationOfRentalRequired,
} from 'common/householdApplication/applicationHelpers';

import { requiredCreditAndBackgroundCheckFieldgroup } from 'common/householdApplication/applicationFormFields';

import {
  getHouseholdPets,
  getStrongestApplicationStatus,
  sortHouseholdsByStrongestApplicationStatus,
  getHouseholdCreditScoreAverage,
} from 'common/householdHelpers';

import { PET_OPTION_VALUE_DOG } from 'common/petOptionItems';

import {
  VERIFICATION_OF_RENTAL_STATUS_OWN,
  VERIFICATION_OF_RENTAL_STATUS_NO_HISTORY,
} from 'common/verificationOfRentalConstants';

import {
  PREQUAL_MINIMUN_CREDIT_SCORE_OK,
  HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION,
  HOUSEHOLD_APPLICATION_CATEGORY_RENTER_PROFILE,
  HOUSEHOLD_APPLICATION_STATUS_INCOME_VERIFIED,
  HOUSEHOLD_APPLICATION_STATUS_PROCESSING,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
  HOUSEHOLD_APPLICATION_STATUS_LIST_ACCEPTED,
} from 'common/householdConstants';

import {
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED,
} from 'common/applicationAssemblyLineConstants';

import { TENANT_SCREENING_CRITERIA_CREDIT_SCORE_QUALIFICATION_AVERAGE } from 'common/tenantScreeningCriteriaConstants';

import log from 'client/lib/log';

const debug = log.debug('sunroom:prequalificationHelpers');

export function isRenterInMoveInDateRange({
  listing,
  householdApplication,
  tenantScreeningCriteria = {},
}) {
  const listingAvailableDateRange = getListingAvailableDateRange({
    listing,
    tenantScreeningCriteria,
  });

  const listingAvailableDateRangeStartDateMoment = momentWithAustinTimezone(
    listingAvailableDateRange.startDate,
  );
  const listingAvailableDateRangeEndDateMoment = momentWithAustinTimezone(
    listingAvailableDateRange.endDate,
  );

  if (householdApplication.movePeriodStartDate && householdApplication.movePeriodEndDate) {
    const renterMovePeriodStartDateMoment = momentWithAustinTimezone(
      householdApplication.movePeriodStartDate,
    ); // eslint-disable-line max-len
    const renterMovePeriodEndDateMoment = momentWithAustinTimezone(
      householdApplication.movePeriodEndDate,
    );
    return (
      (renterMovePeriodStartDateMoment.isSameOrBefore(listingAvailableDateRangeEndDateMoment) &&
        renterMovePeriodStartDateMoment.isSameOrAfter(listingAvailableDateRangeStartDateMoment)) ||
      (renterMovePeriodEndDateMoment.isSameOrBefore(listingAvailableDateRangeEndDateMoment) &&
        renterMovePeriodEndDateMoment.isSameOrAfter(listingAvailableDateRangeStartDateMoment)) ||
      (renterMovePeriodStartDateMoment.isSameOrBefore(listingAvailableDateRangeEndDateMoment) &&
        renterMovePeriodEndDateMoment.isSameOrAfter(listingAvailableDateRangeStartDateMoment))
    );
  }

  if (householdApplication.moveInDate) {
    const renterMoveInDateMoment = momentWithAustinTimezone(householdApplication.moveInDate);
    return (
      renterMoveInDateMoment.isSameOrBefore(listingAvailableDateRangeEndDateMoment) &&
      renterMoveInDateMoment.isSameOrAfter(listingAvailableDateRangeStartDateMoment)
    );
  }

  return false;
}

export function getRenterQualificationByTenantScreeningCriteria({
  listing,
  tenantScreeningCriteria,
  householdApplication,
  guarantorApplications = [],
  guarantorTenantAlertReports = [],
  tenantAlertReport,
  useTenantAlertReport = false,
  excludeGuarantorCreditScore = false,
  excludeCreditScoreQualification = false,
}) {
  typeCheck('listing::NonEmptyObject', listing);
  typeCheck('tenantScreeningCriteria::NonEmptyObject', tenantScreeningCriteria);
  typeCheck('householdApplication::Object', householdApplication);
  typeCheck('guarantorApplications::Array', guarantorApplications);
  typeCheck('guarantorTenantAlertReport::Array', guarantorTenantAlertReports);
  typeCheck('tenantAlertReport::Maybe Object', tenantAlertReport);
  typeCheck('excludeCreditScoreQualification::Maybe Boolean', excludeCreditScoreQualification);

  const requirements = [];
  const applicationPastPendingLanloard = isApplicationPastStatus(
    householdApplication,
    HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
  );
  const guarantorApplication = guarantorApplications[0];

  // Check credit score
  const creditScore = {
    id: 'creditScore',
    title: `${tenantScreeningCriteria.creditScore}+ Credit Score`,
    value: guarantorApplication
      ? guarantorApplication.creditScore
      : householdApplication.creditScore,
    inProgress: true,
  };

  if (useTenantAlertReport) {
    creditScore.value = 0;

    if (guarantorApplication) {
      const guarantorTenantAlertReport = guarantorTenantAlertReports.find(
        ({ userId }) => guarantorApplication.userId === userId,
      );
      if (guarantorTenantAlertReport) {
        creditScore.value = guarantorTenantAlertReport.creditScore;
        creditScore.inProgress = false;
      }
    } else if (tenantAlertReport) {
      creditScore.value = tenantAlertReport.creditScore;
      creditScore.inProgress = false;
    }
  }
  creditScore.isQualified = creditScore.value >= tenantScreeningCriteria.creditScore;

  if (
    householdApplication.guarantorCount &&
    householdApplication.category === HOUSEHOLD_APPLICATION_CATEGORY_RENTER_PROFILE &&
    !excludeGuarantorCreditScore &&
    tenantScreeningCriteria.guarantorAccepted
  ) {
    creditScore.isQualified = true;
  }

  if (excludeCreditScoreQualification) {
    creditScore.isQualified = true;
  }

  if (isApplicationPastStatus(householdApplication, HOUSEHOLD_APPLICATION_STATUS_PROCESSING)) {
    creditScore.inProgress = false;
  }
  requirements.push(creditScore);

  // Monthly income
  const monthlyIncome = {
    id: 'monthlyIncome',
    title: `$${numberWithCommas(
      listing.price * tenantScreeningCriteria.rentToIncomeRatio,
    )}+ per month income`, // eslint-disable-line max-len
    value:
      householdApplication.category === HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION
        ? getApplicationMonthlyIncome({ householdApplication })
        : householdApplication.prequalHouseholdYearlyIncome / 12,
    inProgress: true,
    expectedValue: listing.price * tenantScreeningCriteria.rentToIncomeRatio,
  };
  if (guarantorApplications.length) {
    const guarantorsMonthlyIncome = guarantorApplications.reduce((memo, guarantorApplication) => {
      const guarantorMonthlyIncome =
        guarantorApplication.incomeVerification?.incomeMonthly ||
        getApplicationMonthlyIncome({ householdApplication: guarantorApplication });
      return memo + parseInt(guarantorMonthlyIncome || 0, 10);
    }, 0);

    monthlyIncome.value = guarantorsMonthlyIncome;
    if (guarantorApplication.incomeVerification?.incomeMonthly) {
      monthlyIncome.inProgress = false;
    }
  } else if (
    !householdApplication.hasSourceOfIncome ||
    householdApplication.incomeVerification?.incomeMonthly
  ) {
    monthlyIncome.inProgress = false;
  }
  monthlyIncome.isQualified =
    monthlyIncome.value >= listing.price * tenantScreeningCriteria.rentToIncomeRatio; // eslint-disable-line max-len
  if (householdApplication.guarantorCount && tenantScreeningCriteria.guarantorAccepted) {
    monthlyIncome.isQualified = true;
  }

  if (householdApplication.assemblyLineApplication) {
    const values = getInitialAssemblyLineVOIEFormState(householdApplication);
    const stepStatuses = getAssemblyLineVoieStatuses({
      assemblyLineVoie: values,
      householdApplication,
    });

    if (
      stepStatuses.status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED ||
      stepStatuses.status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED
    ) {
      monthlyIncome.inProgress = false;
    }
  }
  if (isApplicationPastStatus(householdApplication, HOUSEHOLD_APPLICATION_STATUS_PROCESSING)) {
    monthlyIncome.inProgress = false;
  }
  if (guarantorApplications.length) {
    monthlyIncome.inProgress = true;
    if (
      guarantorApplications.every((guarantorApplication) =>
        isApplicationPastStatus(guarantorApplication, HOUSEHOLD_APPLICATION_STATUS_INCOME_VERIFIED),
      )
    ) {
      monthlyIncome.inProgress = false;
    }
  }
  requirements.push(monthlyIncome);

  // Move-in date
  const listingAvailableDateRange = getListingAvailableDateRange({
    listing,
    tenantScreeningCriteria,
  });

  const moveInTitle = isListingTenantScreening(listing)
    ? `Available date is ${moment(listingAvailableDateRange.startDate).format('MMM D')}`
    : `Move-in ${moment(listingAvailableDateRange.startDate).format('MMM D')} ` +
      `- ${moment(listingAvailableDateRange.endDate).format('MMM D')}`;

  const moveInDate = {
    id: 'moveInDate',
    title: moveInTitle,
    value: householdApplication.moveInDate,
    expectedValue: {
      startDate: listingAvailableDateRange.startDate,
      endDate: listingAvailableDateRange.endDate,
    },
  };
  moveInDate.isQualified = isRenterInMoveInDateRange({
    listing,
    householdApplication,
    tenantScreeningCriteria,
  });

  requirements.push(moveInDate);

  // Bankruptcies
  const bankruptcies = {
    id: 'bankruptcies',
    title: `No bankruptcies ${
      (tenantScreeningCriteria.bankruptciesAccepted &&
        tenantScreeningCriteria.bankruptciesDescription) ||
      ''
    }`,
    inProgress: true,
    isQualified: true,
  };
  if (useTenantAlertReport && tenantAlertReport) {
    bankruptcies.isQualified =
      !tenantAlertReport.hasBankruptcyRecord ||
      (moment(tenantAlertReport.bankruptcyRecordDate).isBefore(
        moment().subtract(tenantScreeningCriteria.bankruptciesExpirationInMonths, 'months'),
      ) &&
        tenantScreeningCriteria.bankruptciesAccepted);
    bankruptcies.inProgress = false;
  }
  if (bankruptcies.isQualified) {
    bankruptcies.isQualified =
      !householdApplication.renterPreviouslyFiledForBankruptcy ||
      (moment(householdApplication.renterPreviouslyFiledForBankruptcyDate).isBefore(
        moment().subtract(tenantScreeningCriteria.bankruptciesExpirationInMonths, 'months'),
      ) &&
        tenantScreeningCriteria.bankruptciesAccepted);
  }

  // If bankruptcies are accepted, we are not even showing the bankruptcies question in the requirements list.
  if (
    !tenantScreeningCriteria.bankruptciesAccepted ||
    tenantScreeningCriteria.bankruptciesExpirationInMonths
  ) {
    requirements.push(bankruptcies);
  } else {
    bankruptcies.title = 'Bankruptcies accepted';
    bankruptcies.inProgress = false;
    bankruptcies.isQualified = true;
  }

  // Evictions
  const evictions = {
    id: 'evictions',
    title: `No evictions ${
      (tenantScreeningCriteria.evictionsAccepted && tenantScreeningCriteria.evictionsDescription) ||
      ''
    }`,
    inProgress: true,
    isQualified: true,
  };
  if (useTenantAlertReport && tenantAlertReport) {
    evictions.isQualified =
      !tenantAlertReport.hasEvictionRecord ||
      (moment(tenantAlertReport.evictionRecordDate).isBefore(
        moment().subtract(tenantScreeningCriteria.evictionsExpirationInMonths, 'months'),
      ) &&
        tenantScreeningCriteria.evictionsAccepted);
    evictions.inProgress = false;
  }
  if (evictions.isQualified) {
    evictions.isQualified =
      !householdApplication.renterHasPreviouslyBeenEvicted ||
      (moment(householdApplication.renterHasPreviouslyBeenEvictedDate).isBefore(
        moment().subtract(tenantScreeningCriteria.evictionsExpirationInMonths, 'months'),
      ) &&
        tenantScreeningCriteria.evictionsAccepted);
  }
  requirements.push(evictions);

  const section8 = {
    id: 'section8',
    title: tenantScreeningCriteria.section8Accepted
      ? 'Listing enrolled in Section 8'
      : 'Listing not enrolled in Section 8',
    isQualified:
      tenantScreeningCriteria.section8Accepted || !householdApplication.receiveHousingVouchers,
  };
  requirements.push(section8);

  // Pets
  const maximumPetsAllowed = listing.petsAllowed ? listing.maximumPetsAllowed || 0 : 0;
  const notESApets = ((householdApplication || {}).pets || []).filter(({ isEsa }) => !isEsa);

  const petLimitQualifies = maximumPetsAllowed >= notESApets.length;
  const petsAboveWeight = notESApets.filter((pet) => pet.weight && pet.weight > listing.petWeight)
    .length;
  const petsWeightQualifies = petsAboveWeight === 0;
  const pets = {
    id: 'pets',
    title: listing.petsAllowed
      ? !petsWeightQualifies
        ? `${pluralize('pet', petsAboveWeight, true)} ` +
          `over the weight limit: ${listing.petWeight} lbs`
        : `Max ${pluralize('pets', maximumPetsAllowed, true)} allowed`
      : 'No pets allowed',
    isQualified: petLimitQualifies && petsWeightQualifies,
  };

  requirements.push(pets);

  // Pet Breeds
  if (listing.petsAllowed && tenantScreeningCriteria.breedRestrictions?.length > 0) {
    const renterNonEsaDogBreedsNotAllowed = ((householdApplication || {}).pets || [])
      .filter(({ isEsa, type, breed }) => {
        return (
          type === PET_OPTION_VALUE_DOG &&
          !isEsa &&
          tenantScreeningCriteria.breedRestrictions?.find(({ name }) => name === breed)
        );
      })
      .map(({ breed }) => breed);

    const petBreeds = {
      id: 'petBreeds',
      title:
        renterNonEsaDogBreedsNotAllowed.length === 0
          ? 'Dog Breed'
          : `${pluralize('dog breed', renterNonEsaDogBreedsNotAllowed.length, true)} not allowed`,
      isQualified: renterNonEsaDogBreedsNotAllowed.length === 0,
      tooltipCopy:
        renterNonEsaDogBreedsNotAllowed.length === 0
          ? null
          : `Not allowed: ${renterNonEsaDogBreedsNotAllowed.join(', ')}`,
      expectedValue: {
        breedsNotAllowed: renterNonEsaDogBreedsNotAllowed,
      },
    };

    requirements.push(petBreeds);
  }

  const matches = requirements.filter(({ isQualified }) => isQualified).length;
  const isQualified = requirements.length === matches;

  return {
    matches,
    isQualified,
    requirements: requirements.map((requirement) => ({
      ...requirement,
      inProgress: applicationPastPendingLanloard ? false : requirement.inProgress,
    })),
  };
}

export function getHouseholdQualification({
  moveInDate,
  listing,
  tenantScreeningCriteria,
  renters,
  householdApplications,
  householdGuarantors,
  guarantorApplications,
  tenantAlertReports,
  leadRenterId,
  excludeCreditScoreQualification,
  leaseStartDate,
  partner,
}) {
  typeCheck('moveInDate::DateString', moveInDate);
  typeCheck('listing::NonEmptyObject', listing);
  typeCheck('tenantScreeningCriteria::NonEmptyObject', tenantScreeningCriteria);
  typeCheck('renters::NonEmptyArray', renters);
  typeCheck('householdApplications::NonEmptyArray', householdApplications);
  typeCheck('householdGuarantors::Array', householdGuarantors);
  typeCheck('guarantorApplications::Array', guarantorApplications);
  typeCheck('tenantAlertReports::Array', tenantAlertReports);
  typeCheck('leadRenterId::PositiveNumber', leadRenterId);
  typeCheck('excludeCreditScoreQualification::Maybe Boolean', excludeCreditScoreQualification);
  typeCheck('leaseStartDate::Maybe DateString', leaseStartDate);
  typeCheck('partner::Maybe NonEmptyObject', partner);

  const guarantorByRenterId = householdGuarantors.reduce((memo, householdGuarantor) => {
    memo[householdGuarantor.renterUserId] = memo[householdGuarantor.renterUserId] || [];
    const guarantorApplication = guarantorApplications.find(
      (guarantorApplication) => guarantorApplication.userId === householdGuarantor.guarantorUserId,
    );
    if (guarantorApplication) {
      memo[householdGuarantor.renterUserId].push(guarantorApplication);
    }
    return memo;
  }, {});

  const tenantAlertReportByUserId = tenantAlertReports.reduce(
    (memo, tenantAlertReport) => ({
      ...memo,
      [tenantAlertReport.userId]: tenantAlertReport,
    }),
    {},
  );

  const rentersQualifications = householdApplications.map((householdApplication) => {
    const guarantorApplications = guarantorByRenterId[householdApplication.userId] || [];

    const { isQualified, requirements } = getRenterQualificationByTenantScreeningCriteria({
      listing,
      tenantScreeningCriteria,
      guarantorApplications,
      guarantorTenantAlertReports: guarantorApplications
        .map(({ userId }) => tenantAlertReportByUserId[userId])
        .filter((x) => x),
      householdApplication: householdApplication,
      tenantAlertReport: tenantAlertReportByUserId[householdApplication.userId],
      useTenantAlertReport: true,
    });

    return {
      isQualified,
      userId: householdApplication.userId,
      qualifications: requirements.reduce(
        (memo, requirement) => ({
          ...memo,
          [requirement.id]: {
            isQualified: requirement.isQualified,
            inProgress: requirement.inProgress,
            title: requirement.title,
            value: requirement.value,
          },
        }),
        {},
      ),
    };
  });

  const renterRequirementIds = lodashCompact([
    'creditScore',
    'monthlyIncome',
    'moveInDate',
    (!tenantScreeningCriteria.bankruptciesAccepted ||
      tenantScreeningCriteria.bankruptciesExpirationInMonths) &&
      'bankruptcies',
    'evictions',
    'pets',
    listing.petsAllowed && tenantScreeningCriteria.breedRestrictions?.length > 0 && 'petBreeds',
  ]);

  const { pets } = getHouseholdPets({ householdApplications });

  const householdRequirements = renterRequirementIds.reduce((memo, requirementId) => {
    switch (requirementId) {
      case 'monthlyIncome': {
        let householdMontlyIncome = rentersQualifications.reduce(
          (memo, renter) => memo + renter.qualifications.monthlyIncome.value,
          0,
        );
        let rentToIncomeRatio = tenantScreeningCriteria.rentToIncomeRatio;

        if (guarantorApplications.length) {
          householdMontlyIncome = guarantorApplications.reduce((memo, guarantorApplication) => {
            const guarantorMonthlyIncome =
              guarantorApplication.incomeVerification?.incomeMonthly ||
              guarantorApplication.monthlyIncome;
            return memo + parseInt(guarantorMonthlyIncome || 0, 10);
          }, 0);
          rentToIncomeRatio = tenantScreeningCriteria.guarantorRentToIncomeRatio;
        }
        memo[requirementId] = {
          isQualified: householdMontlyIncome >= listing.price * rentToIncomeRatio, // eslint-disable-line max-len
          value: householdMontlyIncome,
          inProgress: rentersQualifications.some(
            ({ qualifications }) => qualifications[requirementId].inProgress,
          ),
        };

        if (
          guarantorApplications.length &&
          guarantorApplications.every((guarantorApplication) =>
            isApplicationPastStatus(
              guarantorApplication,
              HOUSEHOLD_APPLICATION_STATUS_INCOME_VERIFIED,
            ),
          ) // eslint-disable-line max-len
        ) {
          memo[requirementId].inProgress = false;
        }
        break;
      }
      case 'creditScore': {
        let isCreditScoreOk = false;

        if (
          tenantScreeningCriteria.creditScoreQualification ===
          TENANT_SCREENING_CRITERIA_CREDIT_SCORE_QUALIFICATION_AVERAGE
        ) {
          // eslint-disable-line max-len
          const creditScoreAverage = getHouseholdCreditScoreAverage({
            guarantorApplications,
            tenantAlertReports,
            householdApplications,
          });

          isCreditScoreOk = creditScoreAverage >= tenantScreeningCriteria.creditScore;
        } else {
          isCreditScoreOk = guarantorApplications.length
            ? guarantorApplications.every((guarantorApplication) => {
                const tenantAlertReport = tenantAlertReportByUserId[guarantorApplication.userId];
                return tenantAlertReport
                  ? tenantAlertReport.creditScore >= tenantScreeningCriteria.creditScore
                  : false;
              })
            : rentersQualifications.every(({ qualifications }) => {
                return qualifications[requirementId].isQualified;
              });
        }

        if (excludeCreditScoreQualification) {
          isCreditScoreOk = true;
        }

        memo[requirementId] = {
          isQualified: isCreditScoreOk,
          inProgress: rentersQualifications.some(
            ({ qualifications }) => qualifications[requirementId].inProgress,
          ),
        };

        break;
      }
      case 'moveInDate': {
        if (leaseStartDate) {
          memo[requirementId] = {
            isQualified: true,
            value: leaseStartDate,
            hasLeaseStartDate: true,
          };
        } else {
          const listingAvailableDateMoment = getListingAvailableDateMoment(listing);
          const renterMoveInDateMoment = momentWithAustinTimezone(moveInDate);

          memo[requirementId] = {
            isQualified: renterMoveInDateMoment.isSameOrAfter(listingAvailableDateMoment),
            value: moveInDate,
            hasLeaseStartDate: false,
          };
        }
        break;
      }
      case 'pets': {
        const notESApets = pets.filter(({ isEsa }) => !isEsa);
        const maximumPetsAllowed = listing.maximumPetsAllowed || 0;
        memo[requirementId] = {
          isQualified: maximumPetsAllowed >= notESApets.length,
          title: maximumPetsAllowed
            ? `Max ${pluralize('pets', maximumPetsAllowed, true)} allowed`
            : 'No pets allowed',
        };

        if (memo[requirementId].isQualified && !isNaN(listing.petWeight)) {
          memo[requirementId].isQualified = pets.every((pet) => {
            return (
              pet.type !== PET_OPTION_VALUE_DOG ||
              pet.isEsa ||
              listing.petWeight >= parseInt(pet.weight, 10)
            );
          });
          if (!memo[requirementId].isQualified) {
            memo[requirementId].title = `Dog is over ${listing.petWeight} lbs`;
          }
        }
        break;
      }
      default: {
        memo[requirementId] = {
          isQualified: rentersQualifications.every(
            ({ qualifications }) => qualifications[requirementId]?.isQualified,
          ),
          inProgress: rentersQualifications.some(
            ({ qualifications }) => qualifications[requirementId]?.inProgress,
          ),
        };
      }
    }

    return memo;
  }, {});

  const rentersVerifications = householdApplications.map((householdApplication) => {
    const isVORCompleted = isVerificationOfRentalCompleted({
      householdApplication,
      tenantAlertReport: tenantAlertReportByUserId[householdApplication.userId],
      tenantScreeningCriteria,
    });

    return {
      userId: householdApplication.userId,
      employment: {
        inProgress: !isVerificationOfEmploymentCompleted({ partner, householdApplication }),
      },
      rental: {
        inProgress: !isVORCompleted,
        isQualified:
          householdApplication.rentalStatus === VERIFICATION_OF_RENTAL_STATUS_OWN ||
          householdApplication.rentalStatus === VERIFICATION_OF_RENTAL_STATUS_NO_HISTORY
            ? householdApplication.verificationOfRentalStatus ===
              SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
            : isVORCompleted,
        isRequired: isVerificationOfRentalRequired({
          tenantScreeningCriteria,
          tenantAlertReport: tenantAlertReportByUserId[householdApplication.userId],
        }),
        noHistory: householdApplication.rentalStatus === VERIFICATION_OF_RENTAL_STATUS_NO_HISTORY,
      },
    };
  });
  const guarantorsVerifications = guarantorApplications.map((householdApplication) => {
    return {
      userId: householdApplication.userId,
      employment: {
        inProgress: !isVerificationOfEmploymentCompleted({ partner, householdApplication }),
      },
    };
  });
  const householdVerifications = {
    employment: {
      inProgress: (guarantorsVerifications.length
        ? guarantorsVerifications
        : rentersVerifications
      ).some((verification) => verification.employment.inProgress),
    },
    rental: {
      inProgress: rentersVerifications.some((verification) => verification.rental.inProgress),
      isRequired: rentersVerifications.some((verification) => verification.rental.isRequired),
      isQualified: rentersVerifications.every((verification) => verification.rental.isQualified),
      noHistory: rentersVerifications.every((verification) => verification.rental.noHistory),
    },
  };

  const petsToVerify = pets.filter((pet) => pet.type === 'dog' || pet.type === 'cat');

  if (petsToVerify.length > 0 && partner?.petScreeningUrl) {
    householdVerifications.petScreening = {
      inProgress: petsToVerify.every((pet) => !pet.petScreeningProfileId),
    };
  }

  const isQualified = Object.keys(householdRequirements).every(
    (requirementId) => householdRequirements[requirementId].isQualified,
  );

  const inProgress =
    Object.keys(householdRequirements).some(
      (requirementId) => householdRequirements[requirementId].inProgress,
    ) ||
    Object.keys(householdVerifications).some(
      (requirementId) => householdVerifications[requirementId].inProgress,
    );

  return {
    isQualified,
    inProgress,
    householdRequirements,
    rentersQualifications,
    rentersVerifications,
    guarantorsVerifications,
    householdVerifications,
  };
}

export const LISTING_QUALIFICATION_BUDGET_DISQUALIFIED = 'over budget';
export const LISTING_QUALIFICATION_BUDGET_FITS = 'fits budget';
export const LISTING_QUALIFICATION_BUDGET_HAS_GUARANTOR = 'has guarantor';
export const LISTING_QUALIFICATION_AVAILABLE_MOVE_PERIOD_FITS = 'fits available date range';
export const LISTING_QUALIFICATION_MOVE_PERIOD_BEFORE_FUTURE_LISTING_AVAILABLE_DATE_RANGE =
  'renter move period is before future listing date range'; // eslint-disable-line max-len
export const LISTING_QUALIFICATION_MOVE_PERIOD_AFTER_FUTURE_LISTING_AVAILABLE_DATE_RANGE =
  'renter move period is after future listing date range'; // eslint-disable-line max-len
export const LISTING_QUALIFICATION_MOVE_PERIOD_DOES_NOT_FIT_PAST_LISTING_AVAILABLE_DATE_RANGE =
  'renter move period does not fit past listing date range'; // eslint-disable-line max-len
export const LISTING_QUALIFICATION_PETS_NONE_ALLOWED = 'no pets';
export const LISTING_QUALIFICATION_PETS_TOO_MANY = 'too many pets';
export const LISTING_QUALIFICATION_PETS_FIT = 'pet fit';
export const LISTING_QUALIFICATION_PETS_ZERO = 'zero pets';

export function isRenterQualified(renterProfile) {
  typeCheck('renterProfile::NonEmptyObject', renterProfile);

  return typeof renterProfile.qualificationOverride === 'boolean'
    ? renterProfile.qualificationOverride
    : (renterProfile.creditScore >= PREQUAL_MINIMUN_CREDIT_SCORE_OK ||
        renterProfile.guarantorCount > 0) &&
        renterProfile.renterHasPreviouslyBeenEvicted === false &&
        renterProfile.renterPreviouslyConvitedOfCrime === false;
}

export function isRenterQualifiedForListing(renterProfile, listing, tenantScreeningCriteria) {
  typeCheck('renterProfile::NonEmptyObject', renterProfile);
  typeCheck('listing::NonEmptyObject', listing);
  typeCheck('tenantScreeningCriteria::Maybe Object');

  if (tenantScreeningCriteria) {
    const { isQualified } = getRenterQualificationByTenantScreeningCriteria({
      listing,
      tenantScreeningCriteria,
      householdApplication: renterProfile,
    });
    return isQualified;
  }

  const renterListingQualifications = getRenterFacingQualificationsCopy(renterProfile, listing);

  return Object.keys(renterListingQualifications).every((qualification) => {
    return renterListingQualifications[qualification].isQualified;
  });
}

export function getRenterListingQualifications(renterProfile, listing) {
  typeCheck('renterProfile::NonEmptyObject', renterProfile);
  typeCheck('listing::NonEmptyObject', listing);

  const qualifications = {};

  if (!renterProfile.isPrequalCompleted) {
    return qualifications;
  }

  // Available Date.
  const todayMoment = momentWithAustinTimezone().startOf('day');
  const listingAvailableDateMoment = momentWithAustinTimezone(listing.availableDate);
  const listingAvailableDateRange = getListingAvailableDateRange({ listing });
  const listingAvailableDateRangeStartDateMoment = momentWithAustinTimezone(
    listingAvailableDateRange.startDate,
  ); // eslint-disable-line max-len
  const listingAvailableDateRangeEndDateMoment = momentWithAustinTimezone(
    listingAvailableDateRange.endDate,
  );
  const renterMovePeriodStartDateMoment = momentWithAustinTimezone(
    renterProfile.movePeriodStartDate,
  );
  const renterMovePeriodEndDateMoment = momentWithAustinTimezone(renterProfile.movePeriodEndDate);

  // Renter move-date range fits inside listing available date range.
  if (
    (renterMovePeriodStartDateMoment.isSameOrBefore(listingAvailableDateRangeEndDateMoment) &&
      renterMovePeriodStartDateMoment.isSameOrAfter(listingAvailableDateRangeStartDateMoment)) ||
    (renterMovePeriodEndDateMoment.isSameOrBefore(listingAvailableDateRangeEndDateMoment) &&
      renterMovePeriodEndDateMoment.isSameOrAfter(listingAvailableDateRangeStartDateMoment)) ||
    (renterMovePeriodStartDateMoment.isSameOrBefore(listingAvailableDateRangeEndDateMoment) &&
      renterMovePeriodEndDateMoment.isSameOrAfter(listingAvailableDateRangeStartDateMoment))
  ) {
    qualifications.availableDate = LISTING_QUALIFICATION_AVAILABLE_MOVE_PERIOD_FITS;
  }
  // Listing available date is in the future.
  else if (listingAvailableDateMoment.isAfter(todayMoment)) {
    if (renterMovePeriodEndDateMoment.isBefore(listingAvailableDateRangeStartDateMoment)) {
      qualifications.availableDate = LISTING_QUALIFICATION_MOVE_PERIOD_BEFORE_FUTURE_LISTING_AVAILABLE_DATE_RANGE; // eslint-disable-line max-len
    } else if (renterMovePeriodStartDateMoment.isAfter(listingAvailableDateRangeEndDateMoment)) {
      qualifications.availableDate = LISTING_QUALIFICATION_MOVE_PERIOD_AFTER_FUTURE_LISTING_AVAILABLE_DATE_RANGE; // eslint-disable-line max-len
    }
  }
  // Listing available date is today or in the past.
  else {
    qualifications.availableDate = LISTING_QUALIFICATION_MOVE_PERIOD_DOES_NOT_FIT_PAST_LISTING_AVAILABLE_DATE_RANGE; // eslint-disable-line max-len
  }

  // Price.
  const lowestPrice = renterProfile.prequalHouseholdYearlyIncome / 36;
  if (
    (listing.price && lowestPrice > listing.price) || // For Smart Apartments the price is null.
    (listing.minUnitPrice && lowestPrice > listing.minUnitPrice)
  ) {
    qualifications.price = LISTING_QUALIFICATION_BUDGET_FITS;
  } else if (renterProfile.guarantorCount > 0) {
    qualifications.price = LISTING_QUALIFICATION_BUDGET_HAS_GUARANTOR;
  } else {
    qualifications.price = LISTING_QUALIFICATION_BUDGET_DISQUALIFIED;
  }

  // Pets.
  // We only want to count the non-ESA pets.
  const nonEsaPets = renterProfile.pets.filter((pet) => {
    return !pet.isEsa;
  });
  if (renterProfile.petCount === 0) {
    qualifications.pets = LISTING_QUALIFICATION_PETS_ZERO;
  } else if (!listing.petsAllowed && nonEsaPets.length > 0) {
    qualifications.pets = LISTING_QUALIFICATION_PETS_NONE_ALLOWED;
  } else if (
    typeof listing.maximumPetsAllowed === 'number' &&
    nonEsaPets.length > listing.maximumPetsAllowed
  ) {
    qualifications.pets = LISTING_QUALIFICATION_PETS_TOO_MANY;
  } else {
    qualifications.pets = LISTING_QUALIFICATION_PETS_FIT;
  }

  return qualifications;
}

export function getRenterFacingQualificationsCopy(renterProfile, listing) {
  const renterListingQualifications = getRenterListingQualifications(renterProfile, listing);
  const renterNonEsaPets = (renterProfile.pets || []).filter((pet) => {
    return !pet.isEsa;
  });

  const formattedMoveInPeriod =
    `${momentWithAustinTimezone(renterProfile.movePeriodStartDate).format('MMMM Do')} - ` +
    `${momentWithAustinTimezone(renterProfile.movePeriodEndDate).format('MMMM Do')}`;

  const listingAvailableDateRange = getListingAvailableDateRange({ listing });
  const formattedListingAvailableDateRangeStartDateMoment = moment(
    listingAvailableDateRange.startDate,
  )
    .utc()
    .format('MMMM Do'); // eslint-disable-line max-len
  const formattedListingAvailableDateRangeEndDateMoment = moment(listingAvailableDateRange.endDate)
    .utc()
    .format('MMMM Do'); // eslint-disable-line max-len
  const availableDateQualification = {
    copy:
      renterListingQualifications.availableDate === LISTING_QUALIFICATION_AVAILABLE_MOVE_PERIOD_FITS
        ? 'Move Date: Home is available'
        : 'Move Date: Not available',
    isQualified:
      renterListingQualifications.availableDate ===
      LISTING_QUALIFICATION_AVAILABLE_MOVE_PERIOD_FITS, // eslint-disable-line max-len
    tooltipCopy:
      renterListingQualifications.availableDate ===
      LISTING_QUALIFICATION_MOVE_PERIOD_BEFORE_FUTURE_LISTING_AVAILABLE_DATE_RANGE // eslint-disable-line max-len
        ? `This home is not available until ${formattedListingAvailableDateRangeStartDateMoment}, ` +
          `after your move period (${formattedMoveInPeriod}).`
        : renterListingQualifications.availableDate ===
          LISTING_QUALIFICATION_MOVE_PERIOD_AFTER_FUTURE_LISTING_AVAILABLE_DATE_RANGE // eslint-disable-line max-len
        ? `Since the home is available on ${formattedListingAvailableDateRangeStartDateMoment}, ` +
          `eligible renters must be able to pay rent by ${formattedListingAvailableDateRangeEndDateMoment}. ` + // eslint-disable-line max-len
          `This is before your move period (${formattedMoveInPeriod}).`
        : renterListingQualifications.availableDate ===
          LISTING_QUALIFICATION_MOVE_PERIOD_DOES_NOT_FIT_PAST_LISTING_AVAILABLE_DATE_RANGE // eslint-disable-line max-len
        ? 'This home is available now so you must start paying rent within 2 weeks ' +
          `(${formattedListingAvailableDateRangeEndDateMoment} at the latest). ` +
          `Your current move period is ${formattedMoveInPeriod}.`
        : `This home is available for your move period (${formattedMoveInPeriod}) 👌`,
  };

  const priceQualification = {
    copy:
      renterListingQualifications.price === LISTING_QUALIFICATION_BUDGET_DISQUALIFIED
        ? 'Rent Price: Income too low'
        : 'Rent Price: Fits your income',
    isQualified:
      renterListingQualifications.price === LISTING_QUALIFICATION_BUDGET_FITS ||
      renterListingQualifications.price === LISTING_QUALIFICATION_BUDGET_HAS_GUARANTOR,
    tooltipCopy:
      renterListingQualifications.price === LISTING_QUALIFICATION_BUDGET_DISQUALIFIED
        ? 'Homeowner requires your household monthly income to be at least 3x the monthly rent price'
        : renterListingQualifications.price === LISTING_QUALIFICATION_BUDGET_HAS_GUARANTOR
        ? 'Your guarantor needs a monthly income of at least 5x the rent.'
        : 'Your household income is greater than 3x the monthly rent price 🤑',
  };

  const petsQualification = {
    copy:
      renterListingQualifications.pets === LISTING_QUALIFICATION_PETS_NONE_ALLOWED
        ? 'Pets: No pets allowed'
        : renterListingQualifications.pets === LISTING_QUALIFICATION_PETS_TOO_MANY
        ? `Pets: Only ${listing.maximumPetsAllowed} ${pluralize(
            'pets',
            renterNonEsaPets.length,
          )} allowed`
        : renterNonEsaPets.length === 0
        ? 'Pets: Allowed'
        : `Pets: ${renterNonEsaPets.length} ${pluralize('pets', renterNonEsaPets.length)} allowed`,
    isQualified:
      renterListingQualifications.pets === LISTING_QUALIFICATION_PETS_FIT ||
      renterListingQualifications.pets === LISTING_QUALIFICATION_PETS_ZERO,
    tooltipCopy:
      renterListingQualifications.pets === LISTING_QUALIFICATION_PETS_NONE_ALLOWED
        ? `You have ${renterNonEsaPets.length} pets. This home does not allow pets.`
        : renterListingQualifications.pets === LISTING_QUALIFICATION_PETS_TOO_MANY
        ? `You have ${renterNonEsaPets.length} ${pluralize('pets', renterNonEsaPets.length)}. ` +
          `This home allows ${listing.maximumPetsAllowed} ` +
          `${pluralize('pets', listing.maximumPetsAllowed)} max.`
        : `This home allows ${listing.maximumPetsAllowed} ` +
          `${pluralize('pets', listing.maximumPetsAllowed)} max 🐶🐱`,
  };

  return {
    availableDateQualification,
    priceQualification,
    petsQualification,
  };
}

export function getQualifiedHomesSearchFilters({ renterProfile }) {
  typeCheck('renterProfile::NonEmptyObject', renterProfile);

  const filters = {};

  filters.minRentPrice = undefined;
  filters.maxRentPrice = parseInt(renterProfile.prequalHouseholdYearlyIncome / 36, 10);

  const nonEsaPets = renterProfile.pets.filter((pet) => {
    return !pet.isEsa;
  });
  if (nonEsaPets.length > 0) {
    filters.maximumPetsAllowed = nonEsaPets.length;
  }

  filters.movePeriodStartDate = moment(renterProfile.movePeriodStartDate)
    .utcOffset(0, true)
    .toISOString();
  filters.movePeriodEndDate = moment(renterProfile.movePeriodEndDate)
    .utcOffset(0, true)
    .toISOString();

  return filters;
}

const minimumYearlyIncome = 60000;
const minimumCreditScore = 600;
const renterProfileFieldIdsToCheckForLimitedToursRole = [
  'prequalHouseholdYearlyIncome',
  'creditScore',
  'renterHasPreviouslyBeenEvicted',
  'renterPreviouslyFiledForBankruptcy',
  'renterPreviouslyConvitedOfCrime',
];

export function shouldUserHaveRoleLimitedTours({ renterProfile }) {
  typeCheck('renterProfile::NonEmptyObject', renterProfile);

  if (
    !Object.keys(renterProfile).some(
      (fieldId) =>
        renterProfileFieldIdsToCheckForLimitedToursRole.includes(fieldId) &&
        renterProfile[fieldId] !== null, // eslint-disable-line max-len
    )
  ) {
    return false;
  }

  const isIncomeLow = isNaN(renterProfile.prequalHouseholdYearlyIncome)
    ? false
    : renterProfile.prequalHouseholdYearlyIncome < minimumYearlyIncome;

  const isCreditScoreLow = isNaN(renterProfile.creditScore)
    ? false
    : renterProfile.creditScore < minimumCreditScore;

  return (
    isIncomeLow ||
    isCreditScoreLow ||
    (isBoolean(renterProfile.renterHasPreviouslyBeenEvicted) &&
      renterProfile.renterHasPreviouslyBeenEvicted) || // eslint-disable-line max-len
    (isBoolean(renterProfile.renterPreviouslyFiledForBankruptcy) &&
      renterProfile.renterPreviouslyFiledForBankruptcy) || // eslint-disable-line max-len
    (isBoolean(renterProfile.renterPreviouslyConvitedOfCrime) &&
      renterProfile.renterPreviouslyConvitedOfCrime) // eslint-disable-line max-len
  );
}

export function isRenterQualifiedToRunCreditAndBackgroundReport({ householdApplication }) {
  const requiredFields = householdApplication.noSsn
    ? requiredCreditAndBackgroundCheckFieldgroup.filter((field) => field !== 'ssn')
    : requiredCreditAndBackgroundCheckFieldgroup;

  const missingFields = requiredFields.filter((field) => {
    return !householdApplication[field];
  });

  return {
    missingFields,
    isQualified: missingFields.length === 0,
  };
}

/**
 * Given an array of households, it will return the current/next household to be processing
 *
 * @param {array} households                        An array of Household.
 * @param {boolean} excludeCreditScoreQualification If true, the credit score qualification will not be required to qualify the household.
 * @return {array}
 */
export function getNextQualifiedHousehold({ households, excludeCreditScoreQualification }) {
  if (!households.length) {
    return null;
  }

  const householdsOrderedByStrongestApplicationStatus = sortHouseholdsByStrongestApplicationStatus({
    households,
  });

  const nextHousehold = householdsOrderedByStrongestApplicationStatus[0];
  const nextHouseholdStrongestApplicationStatus = getStrongestApplicationStatus({
    householdApplications: nextHousehold.householdApplications,
  });

  // Return the first approved or waiting for approval household
  if (
    HOUSEHOLD_APPLICATION_STATUS_LIST_ACCEPTED.includes(nextHouseholdStrongestApplicationStatus) ||
    nextHouseholdStrongestApplicationStatus === HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD
  ) {
    return nextHousehold;
  }

  const householdsQualified = households.filter((household) => {
    const householdQualification = getHouseholdQualification({
      moveInDate: household.moveInDate.toISOString(),
      listing: household.listing,
      tenantScreeningCriteria: {
        ...household.tenantScreeningCriteria,
        breedRestrictions: household.breedRestrictions,
      },
      renters: household.renters,
      householdApplications: household.householdApplications,
      householdGuarantors: household.householdGuarantors,
      guarantorApplications: household.guarantorApplications,
      tenantAlertReports: household.tenantAlertReports,
      leadRenterId: household.leadRenterId,
      excludeCreditScoreQualification,
    });

    if (!householdQualification.isQualified) {
      debug('[getNextQualifiedHousehold] not qualified: ', {
        id: household.id,
        householdRequirements: householdQualification.householdRequirements,
      });
    }

    return householdQualification.isQualified;
  });

  const lastApplicationCompletedByHouseholdId = householdsQualified.reduce((memo, household) => {
    return {
      ...memo,
      [household.id]: household.householdApplications
        .map((application) => moment(application.completedDate).unix())
        .sort()
        .pop(),
    };
  }, {});

  // Sort by the first household in completed all their applications
  const householdsSortedByLastApplicationCompleted = householdsQualified.sort(
    (householdA, householdB) => {
      return (
        lastApplicationCompletedByHouseholdId[householdA.id] -
        lastApplicationCompletedByHouseholdId[householdB.id] // eslint-disable-line max-len
      );
    },
  );

  return householdsSortedByLastApplicationCompleted[0];
}

export function getPendingHouseholds({ households }) {
  households.forEach((household) => {
    const tenantAlertReportByUserId = household.tenantAlertReports.reduce(
      (memo, tenantAlertReport) => ({
        ...memo,
        [tenantAlertReport.userId]: tenantAlertReport,
      }),
      {},
    );
    const tenantAlertReportForAllApplicants = [
      ...household.householdApplications,
      ...household.guarantorApplications,
    ].every(({ userId }) => tenantAlertReportByUserId[userId]);

    household.householdQualification = getHouseholdQualification({
      moveInDate: household.moveInDate.toISOString(),
      listing: household.listing,
      tenantScreeningCriteria: {
        ...household.tenantScreeningCriteria,
        breedRestrictions: household.breedRestrictions,
      },
      renters: household.renters,
      householdApplications: household.householdApplications,
      householdGuarantors: household.householdGuarantors,
      guarantorApplications: household.guarantorApplications,
      tenantAlertReports: household.tenantAlertReports,
      leadRenterId: household.leadRenterId,
      excludeCreditScoreQualification: !tenantAlertReportForAllApplicants,
    });

    household.completedDate = moment(
      household.householdApplications.reduce(
        (memo, application) => Math.max(memo, moment(application.completedDate).valueOf()),
        0,
      ),
    ).toISOString();
  });

  households.sort(
    (householdA, householdB) =>
      moment(householdB.completedDate).unix() - moment(householdA.completedDate).unix(),
  );

  return households;
}
